import {
  HIGH_DRIFT_RATE,
  LATENCY_REGRESSION_PCT,
  buildVerdict,
  formatPercent,
  formatSeconds,
  formatSignedPercent,
} from "./evalVerdict";

/**
 * The switch decision, rendered above everything else.
 *
 * A reviewer should be able to answer "can we ship this model?" without
 * scrolling, so the verdict banner states the answer and the tiles below it
 * carry the four numbers that justify it.
 */

const ACCURACY_VERDICT_LABELS = {
  candidate_better: "closer to human than production",
  candidate_worse: "further from human than production",
  tie: "same accuracy as production",
};

const ACCURACY_TONES = {
  candidate_better: "good",
  candidate_worse: "bad",
  tie: "neutral",
};

function Metric({ label, value, delta, detail, tone = "neutral" }) {
  return (
    <div className={`mse-metric mse-metric--${tone}`}>
      <span className="mse-metric__label">{label}</span>
      <span className="mse-metric__row">
        <strong className="mse-metric__value">{value}</strong>
        {delta ? (
          <span className={`mse-metric__delta mse-metric__delta--${tone}`}>
            {delta}
          </span>
        ) : null}
      </span>
      {detail ? <small className="mse-metric__detail">{detail}</small> : null}
    </div>
  );
}

function buildLatencyTile(latency) {
  if (!latency) {
    return {
      value: "—",
      detail: "Not measured on this run",
      tone: "neutral",
    };
  }

  return {
    value: formatSeconds(latency.candidateMedianMs),
    delta: formatSignedPercent(latency.deltaPct),
    detail: `Production ${formatSeconds(latency.baselineMedianMs)} median per loan`,
    tone: latency.deltaPct > LATENCY_REGRESSION_PCT ? "bad" : "neutral",
  };
}

function buildAccuracyTile(accuracy) {
  if (!accuracy) {
    return {
      value: "No labels",
      detail: "Add expected income to a dataset to score accuracy",
      tone: "neutral",
    };
  }

  return {
    value: formatPercent(accuracy.candidateAccuracy),
    delta: `vs ${formatPercent(accuracy.baselineAccuracy)}`,
    detail: `${ACCURACY_VERDICT_LABELS[accuracy.verdict] || accuracy.verdict} · ${accuracy.scored} scored`,
    tone: ACCURACY_TONES[accuracy.verdict] || "neutral",
  };
}

/**
 * Rendered from `summary.metrics`, which reports created before the metrics
 * rollup do not have — those show the run details alone.
 */
export default function MetricsStrip({ summary }) {
  const metrics = summary?.metrics;
  if (!metrics) {
    return null;
  }

  const verdict = buildVerdict(metrics);
  const latencyTile = buildLatencyTile(metrics.latency);
  const accuracyTile = buildAccuracyTile(metrics.accuracy);
  const hasHighSeverity = metrics.highSeverityRate > 0;

  return (
    <section className="mse-decision">
      <div className={`mse-verdict mse-verdict--${verdict.tone}`}>
        <span className="mse-verdict__dot" aria-hidden="true" />
        <div>
          <strong className="mse-verdict__title">{verdict.title}</strong>
          <p className="mse-verdict__reason">{verdict.reason}</p>
        </div>
      </div>

      <div className="mse-metrics">
        <Metric
          label="Income or status changed"
          value={formatPercent(metrics.highSeverityRate)}
          detail={
            hasHighSeverity
              ? "These are the cases to open first"
              : "Nothing borrower-facing moved"
          }
          tone={hasHighSeverity ? "bad" : "good"}
        />
        <Metric
          label="Any drift"
          value={formatPercent(metrics.driftRate)}
          detail="Includes wording-only differences"
          tone={metrics.driftRate > HIGH_DRIFT_RATE ? "warn" : "neutral"}
        />
        <Metric label="Candidate latency" {...latencyTile} />
        <Metric label="Accuracy vs human" {...accuracyTile} />
      </div>
    </section>
  );
}
